import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Ban, CalendarDays, CheckCircle2, MapPin, MessageSquare, MoreHorizontal, Package, Pencil, Play, RotateCcw, Timer, Truck, Users } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "@/components/ui/sonner";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ConflictBanner } from "@/components/common/ConflictBanner";
import { ErrorState } from "@/components/common/ErrorState";
import { LoadingList } from "@/components/common/LoadingList";
import { Field, KeyFigures, Section } from "@/components/common/Section";
import { EntryList } from "@/components/orders/EntryList";
import { VedleggPanel } from "@/components/orders/VedleggPanel";
import { HistorikkPanel } from "@/components/orders/HistorikkPanel";
import { LogistikkCard } from "@/components/orders/LogistikkCard";
import { EstimateSummary } from "@/components/orders/EstimateSummary";
import { ForTimarDialog, LeggTilVarerDialog, MeldUtfortDialog, MerknadDialog, RessursbrukDialog } from "@/components/orders/EntryDialogs";
import { FakturagrunnlagPanel } from "@/components/invoicing/FakturagrunnlagPanel";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage, isConflict } from "@/lib/api";
import { fmtDate, fmtDateTime, fmtMinutes, fmtMoney, fmtNumber } from "@/lib/format";
import { unitShort } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const TAB_CLASS = "relative h-10 shrink-0 rounded-none border-b-2 border-transparent px-3 text-sm font-medium text-muted-foreground shadow-none hover:text-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none";
const Count = ({ value }) => <span className="tabular ml-1.5 rounded bg-secondary px-1 py-px text-[11px] text-muted-foreground">{value}</span>;

/** Estimat vs faktisk – enkel samanlikning for administrasjon. */
const EstimatVsFaktisk = ({ estimate, stats, goodsEntries }) => {
  const plannedMin = estimate?.total_hours ? Math.round(estimate.total_hours * 60) : 0;
  const actualMin = (stats.resource_minutes || 0) + (stats.minutes || 0);
  const items = (estimate?.inputs?.items || []).length
    ? estimate.inputs.items
    : estimate?.inputs?.item_id
      ? [{ catalog_item_id: estimate.inputs.item_id, quantity: estimate.inputs.quantity, unit: estimate.inputs.unit, item_name: estimate.inputs.item_name }]
      : [];
  const rows = [
    { label: "Transport og timar", planned: plannedMin ? fmtMinutes(plannedMin) : "–", actual: fmtMinutes(actualMin), diff: plannedMin ? actualMin - plannedMin : null, fmt: (d) => `${d > 0 ? "+" : "−"}${fmtMinutes(Math.abs(d))}` },
    ...items.map((it) => {
      const planned = Number(it.quantity) || 0;
      const actual = goodsEntries.filter((e) => e.catalog_item_id === it.catalog_item_id).reduce((sum, e) => sum + (Number(e.quantity) || 0), 0);
      const u = unitShort(it.unit);
      return { label: it.item_name || "Vare", planned: planned ? `${fmtNumber(planned, 2)} ${u}` : "–", actual: `${fmtNumber(actual, 2)} ${u}`, diff: planned ? actual - planned : null, fmt: (d) => `${d > 0 ? "+" : "−"}${fmtNumber(Math.abs(d), 2)} ${u}` };
    }),
  ];
  return (
    <table className="w-full text-sm" data-testid="estimat-vs-faktisk">
      <thead>
        <tr className="text-left text-xs text-muted-foreground">
          <th className="pb-2 font-medium">Post</th>
          <th className="pb-2 text-right font-medium">Kalkyle</th>
          <th className="pb-2 text-right font-medium">Registrert</th>
          <th className="pb-2 text-right font-medium">Avvik</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-border border-t border-border">
        {rows.map((r) => (
          <tr key={r.label}>
            <td className="py-2 pr-2">{r.label}</td>
            <td className="num py-2 text-right text-muted-foreground">{r.planned}</td>
            <td className="num py-2 text-right">{r.actual}</td>
            <td className={cn("num py-2 text-right", r.diff === null ? "text-muted-foreground" : r.diff > 0 ? "text-warning" : r.diff < 0 ? "text-success" : "")}>{r.diff === null ? "–" : r.diff === 0 ? "0" : r.fmt(r.diff)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default function OrdreDetaljPage() {
  const { orderId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isAdmin } = useAuth();
  const tab = searchParams.get("tab") || "oversikt";
  const [dialog, setDialog] = useState(null);
  const [statusDialog, setStatusDialog] = useState(null);
  const [statusNote, setStatusNote] = useState("");
  const [conflict, setConflict] = useState(null);

  const orderQuery = useQuery({ queryKey: ["order", orderId], queryFn: async () => (await api.get(`/orders/${orderId}`)).data });
  const entriesQuery = useQuery({ queryKey: ["entries", orderId], queryFn: async () => (await api.get(`/orders/${orderId}/entries`)).data, enabled: orderQuery.isSuccess });
  const order = orderQuery.data;

  const statusMutation = useMutation({
    mutationFn: async ({ action, note }) => (await api.post(`/orders/${orderId}/status`, { action, note: note || null, version: order.version })).data,
    onSuccess: (data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["order", orderId] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["counts"] });
      queryClient.invalidateQueries({ queryKey: ["history", orderId] });
      queryClient.invalidateQueries({ queryKey: ["invoicing"] });
      const msg = { start: "Ordra er sett i arbeid", avlys: "Ordra er avlyst", gjenopne: "Ordra er gjenopna" }[vars.action];
      toast.success(msg);
      setStatusDialog(null);
      setStatusNote("");
    },
    onError: (err) => {
      if (isConflict(err)) setConflict(errorMessage(err));
      else toast.error("Handlinga feila", { description: errorMessage(err) });
      setStatusDialog(null);
    },
  });

  const setTab = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value === "oversikt") next.delete("tab");
    else next.set("tab", value);
    setSearchParams(next, { replace: true });
  };

  if (orderQuery.isLoading) return <LoadingList rows={3} />;
  if (orderQuery.isError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" className="h-10" onClick={() => navigate(-1)}><ArrowLeft className="mr-2 h-4 w-4" /> Tilbake</Button>
        <ErrorState message={errorMessage(orderQuery.error)} onRetry={() => orderQuery.refetch()} testId="order-error" />
      </div>
    );
  }

  const entries = entriesQuery.data || [];
  const timeEntries = entries.filter((e) => e.type === "timar" || e.type === "ressurs");
  const goodsEntries = entries.filter((e) => e.type === "vare");
  const noteEntries = entries.filter((e) => e.type === "merknad");
  const open = order.status === "ny" || order.status === "i_arbeid";
  const canRegister = order.status !== "avlyst" && (isAdmin || open);
  const isResponsible = !order.responsible_user || order.responsible_user.id === user?.id;
  const canMeldUtfort = open && (isAdmin || isResponsible);
  const stats = order.stats || {};
  const estimate = isAdmin ? order.estimate : null;
  const canStart = order.status === "ny";
  const canCancel = order.status !== "avlyst" && !(order.invoice_status && ["godkjent", "eksportert", "fakturert"].includes(order.invoice_status));
  const canReopen = (order.status === "utfort" || order.status === "avlyst") && order.invoice_status !== "fakturert";
  const place = order.logistics?.delivery?.name || order.site_address;

  const reload = () => {
    setConflict(null);
    orderQuery.refetch();
    entriesQuery.refetch();
  };

  const actionButtons = (
    <>
      {canRegister ? (
        <>
          <Button className="h-11 flex-1 md:h-10 md:flex-none" onClick={() => setDialog({ type: "timar" })} data-testid="ordre-for-timar-button"><Timer className="mr-2 h-4 w-4" /> Før timar</Button>
          <Button variant="outline" className="h-11 flex-1 bg-card md:h-10 md:flex-none" onClick={() => setDialog({ type: "vare" })} data-testid="ordre-legg-til-varer-button"><Package className="mr-2 h-4 w-4" /> Legg til varer</Button>
        </>
      ) : null}
      {canMeldUtfort ? (
        <Button variant="outline" className="h-11 flex-1 border-success bg-card text-success hover:bg-tint-success hover:text-success md:h-10 md:flex-none" onClick={() => setDialog({ type: "meld_utfort" })} data-testid="ordre-meld-utfort-button"><CheckCircle2 className="mr-2 h-4 w-4" /> Meld utført</Button>
      ) : null}
    </>
  );

  return (
    <div className="space-y-5" data-testid="ordre-detalj-page">
      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" size="sm" className="-ml-2 h-9 px-2 text-muted-foreground hover:text-foreground" asChild>
          <Link to={isAdmin ? "/ordrar" : "/mine-ordrar"} data-testid="ordre-tilbake-link"><ArrowLeft className="mr-1.5 h-4 w-4" /> {isAdmin ? "Ordrar" : "Mine ordrar"}</Link>
        </Button>
        {isAdmin ? (
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" className="h-9 bg-card" onClick={() => navigate(`/ordrar/${order.id}/endre`)} data-testid="ordre-endre-button"><Pencil className="mr-1.5 h-4 w-4" /> Endre</Button>
            {canStart ? <Button variant="outline" size="sm" className="hidden h-9 bg-card md:inline-flex" onClick={() => statusMutation.mutate({ action: "start" })} data-testid="ordre-start-button"><Play className="mr-1.5 h-4 w-4" /> Set i arbeid</Button> : null}
            {canCancel ? <Button variant="outline" size="sm" className="hidden h-9 bg-card text-destructive hover:text-destructive md:inline-flex" onClick={() => setStatusDialog("avlys")} data-testid="ordre-avlys-button"><Ban className="mr-1.5 h-4 w-4" /> Avlys</Button> : null}
            {canReopen ? <Button variant="outline" size="sm" className="hidden h-9 bg-card md:inline-flex" onClick={() => setStatusDialog("gjenopne")} data-testid="ordre-gjenopne-button"><RotateCcw className="mr-1.5 h-4 w-4" /> Gjenopne</Button> : null}
            {canStart || canCancel || canReopen ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="h-9 w-9 bg-card md:hidden" aria-label="Fleire handlingar" data-testid="ordre-meir-button"><MoreHorizontal className="h-4 w-4" /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[200px] bg-popover" data-testid="ordre-meir-menu">
                  {canStart ? <DropdownMenuItem className="min-h-[44px]" onSelect={() => statusMutation.mutate({ action: "start" })} data-testid="ordre-start-menu-item"><Play className="mr-2 h-4 w-4" /> Set i arbeid</DropdownMenuItem> : null}
                  {canReopen ? <DropdownMenuItem className="min-h-[44px]" onSelect={() => setStatusDialog("gjenopne")} data-testid="ordre-gjenopne-menu-item"><RotateCcw className="mr-2 h-4 w-4" /> Gjenopne</DropdownMenuItem> : null}
                  {canCancel ? <DropdownMenuItem className="min-h-[44px] text-destructive focus:text-destructive" onSelect={() => setStatusDialog("avlys")} data-testid="ordre-avlys-menu-item"><Ban className="mr-2 h-4 w-4" /> Avlys ordra</DropdownMenuItem> : null}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        ) : null}
      </div>

      {conflict ? <ConflictBanner message={conflict} onReload={reload} /> : null}

      <header data-testid="ordre-header">
        <div className="flex flex-wrap items-center gap-2">
          <span className="num text-sm font-medium text-muted-foreground" data-testid="ordre-nummer">{order.order_number}</span>
          <StatusBadge status={order.status} data-testid="ordre-status" />
          {isAdmin && order.invoice_status && order.invoice_status !== "ingen" ? <StatusBadge kind="invoice" status={order.invoice_status} data-testid="ordre-fakturastatus" /> : null}
          <span className="text-xs text-muted-foreground">{order.template_label}</span>
        </div>
        <h1 className="mt-2 text-[22px] font-semibold leading-[1.15] md:text-[28px]" data-testid="ordre-tittel">{order.title}</h1>
        <p className="mt-1 text-[15px] text-muted-foreground" data-testid="ordre-kunde">{order.customer_name}</p>
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-4 w-4" /> <span className="tabular text-foreground">{fmtDate(order.planned_date)}</span>{order.logistics?.planned_time ? <span className="tabular text-foreground">kl. {order.logistics.planned_time}</span> : null}</span>
          {place ? <span className="inline-flex min-w-0 max-w-full items-center gap-1.5"><MapPin className="h-4 w-4 shrink-0" /> <span className="truncate text-foreground">{place}</span></span> : null}
          <span className="inline-flex min-w-0 max-w-full items-center gap-1.5"><Users className="h-4 w-4 shrink-0" /> <span className="truncate"><span className="text-foreground">{order.assigned_users?.map((u) => u.name).join(", ") || "Ingen tildelte"}</span>{order.responsible_user ? ` · ansvarleg ${order.responsible_user.name.split(" ")[0]}` : ""}</span></span>
        </div>
        <div className="mt-4 hidden flex-wrap gap-2 md:flex" data-testid="ordre-actions-desktop">{actionButtons}</div>
      </header>

      <Tabs value={tab} onValueChange={setTab} data-testid="ordre-detalj-tabs">
        <TabsList className="scrollbar-none -mx-4 flex h-auto w-[calc(100%+2rem)] justify-start gap-1 overflow-x-auto rounded-none border-b border-border bg-transparent px-4 py-0 md:mx-0 md:w-full md:px-0">
          <TabsTrigger value="oversikt" className={TAB_CLASS} data-testid="tab-oversikt">Oversikt</TabsTrigger>
          <TabsTrigger value="timar" className={TAB_CLASS} data-testid="tab-timar">Timar<Count value={timeEntries.length} /></TabsTrigger>
          <TabsTrigger value="varer" className={TAB_CLASS} data-testid="tab-varer">Varer<Count value={goodsEntries.length} /></TabsTrigger>
          <TabsTrigger value="vedlegg" className={TAB_CLASS} data-testid="tab-vedlegg">Vedlegg<Count value={order.attachments_count} /></TabsTrigger>
          <TabsTrigger value="historikk" className={TAB_CLASS} data-testid="tab-historikk">Historikk</TabsTrigger>
          {isAdmin ? <TabsTrigger value="kalkyle" className={TAB_CLASS} data-testid="tab-kalkyle">Kalkyle</TabsTrigger> : null}
          {isAdmin ? <TabsTrigger value="fakturagrunnlag" className={TAB_CLASS} data-testid="tab-fakturagrunnlag">Fakturagrunnlag</TabsTrigger> : null}
        </TabsList>

        <TabsContent value="oversikt" className="mt-4 space-y-4">
          <KeyFigures
            testId="ordre-nokkeltal"
            items={[
              { label: "Timar", value: fmtMinutes(stats.minutes || 0), testId: "stat-timar" },
              { label: "Køyretøy/maskin", value: fmtMinutes(stats.resource_minutes || 0), testId: "stat-ressurs" },
              { label: "Varelinjer", value: stats.goods_count || 0, testId: "stat-varer" },
              { label: "Merknader", value: stats.notes_count || 0, testId: "stat-merknader" },
            ]}
          />
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="space-y-4 lg:col-span-2">
              <Section title="Oppdrag">
                <p className="whitespace-pre-wrap text-sm leading-6" data-testid="ordre-skildring">{order.description || "Inga skildring."}</p>
                <div className="mt-4 grid grid-cols-2 gap-4 border-t border-border pt-4">
                  <Field label="Kunde"><p className="font-medium">{order.customer?.name}</p>{order.customer?.address ? <p className="text-muted-foreground">{order.customer.address}</p> : null}{order.customer?.contact_name ? <p className="text-muted-foreground">{order.customer.contact_name}{order.customer.contact_phone ? ` · ${order.customer.contact_phone}` : ""}</p> : null}</Field>
                  <Field label="Kundereferanse">{order.customer_reference || "–"}</Field>
                  <Field label="Oppretta"><span className="tabular">{fmtDateTime(order.created_at)}</span>{order.created_by_name ? <span className="text-muted-foreground"> · {order.created_by_name}</span> : null}</Field>
                  {order.completed_at ? <Field label="Meld utført" testId="ordre-utfort-info"><span className="tabular">{fmtDateTime(order.completed_at)}</span> · {order.completed_by_name}{order.completion_note ? <p className="text-muted-foreground">«{order.completion_note}»</p> : null}</Field> : null}
                  {order.cancelled_at ? <Field label="Avlyst" testId="ordre-avlyst-info"><span className="tabular">{fmtDateTime(order.cancelled_at)}</span>{order.cancel_reason ? <p className="text-muted-foreground">«{order.cancel_reason}»</p> : null}</Field> : null}
                </div>
              </Section>
              <LogistikkCard logistics={order.logistics} />
            </div>
            <div className="space-y-4">
              {isAdmin ? (
                <Section title="Prising og fakturering" testId="ordre-prising-kort">
                  <div className="space-y-3">
                    <Field label="Prising">{order.pricing_mode === "fixed" ? `Fastpris ${fmtMoney(order.fixed_price)} eks. mva` : "Per eining"}</Field>
                    {estimate ? <Field label="Kalkyle (overslag eks. mva)"><button type="button" className="num font-medium hover:underline" onClick={() => setTab("kalkyle")} data-testid="ordre-kalkyle-sum">{fmtMoney(estimate.subtotal_excl_vat)}</button></Field> : null}
                    <Field label="Fakturastatus"><StatusBadge kind="invoice" status={order.invoice_status || "ingen"} />{order.invoice_reference ? <span className="num ml-2 text-xs text-muted-foreground">Faktura {order.invoice_reference}</span> : null}</Field>
                    {order.internal_notes ? <Field label="Interne notat">{order.internal_notes}</Field> : null}
                  </div>
                  <Button variant="outline" size="sm" className="mt-4 h-9 w-full bg-card" onClick={() => setTab("fakturagrunnlag")} data-testid="ordre-til-fakturagrunnlag-button">Opne fakturagrunnlag</Button>
                </Section>
              ) : null}
              <Section
                title="Merknader"
                actions={canRegister ? <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setDialog({ type: "merknad" })} data-testid="ordre-ny-merknad-button"><MessageSquare className="mr-1.5 h-4 w-4" /> Ny</Button> : null}
                padded={false}
              >
                <div className="p-3">
                  {entriesQuery.isLoading ? <LoadingList rows={1} /> : <EntryList order={order} entries={noteEntries} canEdit={canRegister} onEdit={(entry) => setDialog({ type: "merknad", entry })} emptyTitle="Ingen merknader" testId="merknad-list" />}
                </div>
              </Section>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="timar" className="mt-4 space-y-3">
          {canRegister ? (
            <div className="flex flex-wrap gap-2">
              <Button className="h-11 md:h-10" onClick={() => setDialog({ type: "timar" })} data-testid="timar-for-timar-button"><Timer className="mr-2 h-4 w-4" /> Før timar</Button>
              <Button variant="outline" className="h-11 bg-card md:h-10" onClick={() => setDialog({ type: "ressurs" })} data-testid="timar-ressurs-button"><Truck className="mr-2 h-4 w-4" /> Køyretøy/maskin</Button>
            </div>
          ) : null}
          {entriesQuery.isLoading ? <LoadingList rows={2} /> : (
            <EntryList order={order} entries={timeEntries} canEdit={canRegister} onEdit={(entry) => setDialog({ type: entry.type, entry })} emptyTitle="Ingen timar førte" emptyDescription={isAdmin ? "Ingen timar eller ressursbruk er registrert på ordra." : "Du har ikkje ført timar på denne ordra enno."} testId="timar-list" />
          )}
          {!isAdmin ? <p className="text-xs text-muted-foreground">Du ser berre dine eigne timar og ressursbruk.</p> : null}
        </TabsContent>

        <TabsContent value="varer" className="mt-4 space-y-3">
          {canRegister ? <Button className="h-11 md:h-10" onClick={() => setDialog({ type: "vare" })} data-testid="varer-legg-til-button"><Package className="mr-2 h-4 w-4" /> Legg til varer</Button> : null}
          {entriesQuery.isLoading ? <LoadingList rows={2} /> : <EntryList order={order} entries={goodsEntries} canEdit={canRegister} onEdit={(entry) => setDialog({ type: "vare", entry })} emptyTitle="Ingen varer" emptyDescription="Legg til massar, delar eller anna forbruk frå katalogen." testId="varer-list" />}
        </TabsContent>

        <TabsContent value="vedlegg" className="mt-4">
          <VedleggPanel order={order} canEdit={canRegister} />
        </TabsContent>

        <TabsContent value="historikk" className="mt-4">
          <HistorikkPanel order={order} isAdmin={isAdmin} />
        </TabsContent>

        {isAdmin ? (
          <TabsContent value="kalkyle" className="mt-4">
            <div className="grid gap-4 lg:grid-cols-3">
              <Section
                title="Kalkyle (låst snapshot)"
                description={estimate ? `Rekna ${fmtDateTime(estimate.created_at)}${estimate.created_by_name ? ` av ${estimate.created_by_name}` : ""}. Endrar seg ikkje om katalogprisar blir justerte.` : "Ingen kalkyle er lagra på ordra."}
                actions={<Button variant="outline" size="sm" className="h-8 bg-card" onClick={() => navigate(`/ordrar/${order.id}/endre`)} data-testid="kalkyle-endre-button"><Pencil className="mr-1.5 h-3.5 w-3.5" /> {estimate ? "Rekn på nytt" : "Lag kalkyle"}</Button>}
                className="lg:col-span-2"
                testId="ordre-kalkyle"
              >
                <EstimateSummary estimate={estimate} emptyText="Opne «Lag kalkyle» for å leggje inn mengd, bil og rute. Kalkylen blir lagra som eit låst overslag på ordra." testId="ordre-estimate" />
              </Section>
              <Section title="Estimat vs. registrert" description="Samanliknar kalkylen med det mannskapet har ført." testId="ordre-estimat-vs-faktisk">
                {estimate ? <EstimatVsFaktisk estimate={estimate} stats={stats} goodsEntries={goodsEntries} /> : <p className="text-sm text-muted-foreground">Lag ein kalkyle for å samanlikne.</p>}
                <p className="mt-3 text-xs leading-5 text-muted-foreground">Fakturagrunnlaget blir alltid laga frå registreringane, ikkje frå kalkylen.</p>
              </Section>
            </div>
          </TabsContent>
        ) : null}

        {isAdmin ? (
          <TabsContent value="fakturagrunnlag" className="mt-4">
            <FakturagrunnlagPanel order={order} />
          </TabsContent>
        ) : null}
      </Tabs>

      {canRegister || canMeldUtfort ? (
        <>
          <div className="h-16 md:hidden" aria-hidden="true" />
          <div className="safe-bottom fixed inset-x-0 bottom-16 z-20 flex gap-2 border-t border-border bg-card px-4 py-2.5 md:hidden" data-testid="ordre-actions-mobile">{actionButtons}</div>
        </>
      ) : null}

      {dialog?.type === "timar" ? <ForTimarDialog open onOpenChange={(o) => !o && setDialog(null)} order={order} entry={dialog.entry || null} /> : null}
      {dialog?.type === "ressurs" ? <RessursbrukDialog open onOpenChange={(o) => !o && setDialog(null)} order={order} entry={dialog.entry || null} /> : null}
      {dialog?.type === "vare" ? <LeggTilVarerDialog open onOpenChange={(o) => !o && setDialog(null)} order={order} entry={dialog.entry || null} /> : null}
      {dialog?.type === "merknad" ? <MerknadDialog open onOpenChange={(o) => !o && setDialog(null)} order={order} entry={dialog.entry || null} /> : null}
      {dialog?.type === "meld_utfort" ? <MeldUtfortDialog open onOpenChange={(o) => !o && setDialog(null)} order={order} /> : null}

      <AlertDialog open={Boolean(statusDialog)} onOpenChange={(o) => !o && setStatusDialog(null)}>
        <AlertDialogContent className="bg-card" data-testid="ordre-status-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{statusDialog === "avlys" ? "Avlyse ordra?" : "Gjenopne ordra?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {statusDialog === "avlys" ? "Registreringar og historikk blir tekne vare på. Grunngjeving er påkravd." : "Ordra blir sett tilbake til I arbeid slik at registreringar kan endrast."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {statusDialog === "avlys" ? (
            <div className="space-y-2">
              <Label htmlFor="avlys-grunn">Grunngjeving *</Label>
              <Textarea id="avlys-grunn" value={statusNote} onChange={(e) => setStatusNote(e.target.value)} rows={3} className="bg-card" data-testid="ordre-avlys-grunn" />
            </div>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel className="h-11 bg-card md:h-10">Avbryt</AlertDialogCancel>
            <AlertDialogAction
              className="h-11 md:h-10"
              disabled={statusDialog === "avlys" && !statusNote.trim()}
              onClick={(e) => { e.preventDefault(); statusMutation.mutate({ action: statusDialog, note: statusNote }); }}
              data-testid="ordre-status-confirm"
            >
              {statusDialog === "avlys" ? "Avlys ordra" : "Gjenopne"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
